import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'https://dev.api.sportona.com').replace(/\/+$/, '');
const BASE_URL = `${API_BASE}/api/v1/`;

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Messaging API Service
 */
export const messagingService = {
  /**
   * Fetch conversations for a specific role
   * @param {string} role - 'customer', 'vendor', or 'staff'
   */
  getConversations: async (role) => {
    try {
      const response = await api.get(`${role}/messages/conversations`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Fetch messages for a specific conversation
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @param {number|string} conversationId
   * @param {number} offset
   * @param {number} limit
   */
  getMessages: async (role, conversationId, offset = 0, limit = 50) => {
    try {
      const response = await api.get(`${role}/messages/conversations/${conversationId}`, {
        params: { offset, limit },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Send a message
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @param {object} payload - { conversation_id, content } OR { receiver_type, receiver_id, content }
   */
  sendMessage: async (role, payload) => {
    try {
      const response = await api.post(`${role}/messages/send`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Mark a conversation as read
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @param {number|string} conversationId
   */
  markAsRead: async (role, conversationId) => {
    try {
      const response = await api.patch(`${role}/messages/conversations/${conversationId}/read`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Initialize a manual batch chat
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @param {number|string} batchId
   */
  createBatchConversation: async (role, batchId) => {
    try {
      const response = await api.post(`${role}/messages/conversations/batch`, { batch_id: batchId });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Add a participant to a conversation
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @param {number|string} conversationId
   * @param {object} payload - { user_id, user_type, role }
   */
  addParticipant: async (role, conversationId, payload) => {
    try {
      const response = await api.post(`${role}/messages/conversations/${conversationId}/participants`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Delete a message (Moderation)
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @param {number|string} messageId
   */
  deleteMessage: async (role, messageId) => {
    try {
      const response = await api.delete(`${role}/messages/${messageId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Delete an entire conversation
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @param {number|string} conversationId
   */
  deleteConversation: async (role, conversationId) => {
    try {
      const response = await api.delete(`${role}/messages/conversations/${conversationId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  },

  /**
   * Get WebSocket URL based on current API base
   * @param {string} role - 'customer', 'vendor', or 'staff'
   * @returns {string}
   */
  getWebSocketURL: (role) => {
    const wsBase = API_BASE.replace(/^http/, 'ws');
    // Ensure role is mapped correctly for WS endpoints if needed
    // Vendor dashboard uses 'vendor', customer uses 'customer'
    return `${wsBase}/api/v1/${role}/ws`;
  },
};

export default messagingService;
